import { Locale } from "@redotech/crdb-enums/common/locale";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { getLocaleName } from "@redotech/redo-i18n/locales";
import AiStarOrangeIcon from "@redotech/redo-icons/ai-star-orange.svg";
import {
  TranslationFieldInput,
  TranslationOriginalValue,
} from "@redotech/redo-merchant-app-common/components/translations/translation-field-editors";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { alertMessage } from "@redotech/redo-web/alert";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useMemo, useState } from "react";
import {
  flowStepTranslationsQueryKey,
  selectedTranslationLanguages,
  TRANSLATIONS_STALE_TIME,
} from "./step-translations";
import {
  computeTranslationSaveDiff,
  FlowTranslatableFieldSpec,
  indexTranslations,
  languageFieldKey,
} from "./translation-save-diff";

interface TranslationsDraft {
  values: Record<string, string>;
  savedValues: Record<string, string>;
  savedSourceTexts: Record<string, string>;
  aiKeys: ReadonlySet<string>;
}

export const FieldTranslationsModal = memo(function FieldTranslationsModal({
  elementId,
  stepElementIds,
  field,
  flowType,
  canEdit,
  onClose,
}: {
  elementId: string;
  stepElementIds: readonly string[];
  field: FlowTranslatableFieldSpec;
  flowType: FlowType;
  canEdit: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: flowStepTranslationsQueryKey(stepElementIds),
    queryFn: async () => {
      const { translations } = await rpcClient.getFlowElementTranslations({
        elementIds: [...stepElementIds],
      });
      return translations;
    },
    staleTime: TRANSLATIONS_STALE_TIME,
  });
  const selectedLanguages = useMemo(
    () => selectedTranslationLanguages(query.data ?? [], i18n.language),
    [query.data, i18n.language],
  );
  const indexed = useMemo(
    () =>
      query.data &&
      indexTranslations(
        query.data.filter((translation) => translation.elementId === elementId),
      ),
    [query.data, elementId],
  );

  // Seed once; a background refetch must not clobber unsaved edits.
  const [draft, setDraft] = useState<TranslationsDraft | null>(null);
  useEffect(() => {
    if (!draft && indexed) {
      setDraft({
        values: { ...indexed.values },
        savedValues: indexed.values,
        savedSourceTexts: indexed.sourceTexts,
        aiKeys: indexed.aiKeys,
      });
    }
  }, [draft, indexed]);

  const setTranslation = useHandler((language: Locale, value: string) => {
    const key = languageFieldKey(language, field.field);
    setDraft((draft) => {
      if (!draft) {
        return draft;
      }
      const aiKeys = new Set(draft.aiKeys);
      aiKeys.delete(key);
      return { ...draft, values: { ...draft.values, [key]: value }, aiKeys };
    });
  });

  const { writes, deletes } = computeTranslationSaveDiff({
    elementId,
    flowType,
    fields: [field],
    languages: selectedLanguages,
    values: draft?.values ?? {},
    savedValues: draft?.savedValues ?? {},
    savedSourceTexts: draft?.savedSourceTexts ?? {},
    aiKeys: draft?.aiKeys ?? new Set(),
  });
  const dirty = writes.length > 0 || deletes.length > 0;

  const save = useMutation({
    mutationFn: async () => {
      await rpcClient.setFlowElementTranslations({ writes, deletes });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: flowStepTranslationsQueryKey(stepElementIds),
      });
      onClose();
    },
    onError: () =>
      alertMessage(t("Saving translations failed. Please try again.")),
  });

  return (
    <RedoModal
      contentScrollable
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: t("Save"),
        size: "sm",
        disabled: !canEdit || !dirty,
        pending: save.isPending,
        onClick: () => save.mutate(),
      }}
      secondaryButton={{ text: t("Cancel"), size: "sm", onClick: onClose }}
      subtitle={t(
        "See translations for your selected languages. Translations are generated by AI by default, but you can manually edit them at any time.",
      )}
      title={t("{{field}} translations", { field: field.label })}
    >
      {query.error ? (
        <Text fontSize="sm" textColor="error">
          {t("Failed to load translations.")}
        </Text>
      ) : !draft ? (
        <Text fontSize="sm" textColor="secondary">
          {t("Loading translations…")}
        </Text>
      ) : (
        <Flex dir="column" gap="xl">
          <Flex dir="column" gap="md">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {t("Original input")}
            </Text>
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                {t("English")}
              </Text>
              <TranslationOriginalValue editorKey={elementId} field={field} />
            </Flex>
          </Flex>
          <Divider />
          <Flex dir="column" gap="md">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {t("Translation")}
            </Text>
            {!selectedLanguages.length && (
              <Text fontSize="xs" textColor="secondary">
                {t(
                  "No languages selected yet. Choose languages in the step's Translation section to generate translations.",
                )}
              </Text>
            )}
            <Flex dir="column" gap="2xl">
              {selectedLanguages.map((language) => {
                const key = languageFieldKey(language, field.field);
                return (
                  <Flex dir="column" gap="sm" key={language}>
                    <Flex align="center" gap="xxs">
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        textColor="secondary"
                      >
                        {getLocaleName(language, i18n.language)}
                      </Text>
                      {draft.aiKeys.has(key) && <AiStarOrangeIcon />}
                    </Flex>
                    <TranslationFieldInput
                      disabled={!canEdit}
                      editorKey={elementId}
                      field={field}
                      fullWidth
                      language={language}
                      onChange={(value) => setTranslation(language, value)}
                      value={draft.values[key] ?? ""}
                    />
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        </Flex>
      )}
    </RedoModal>
  );
});
